import { SKY_STAGES } from './76-sky-campaign.mjs';

const axes = (x = false, y = false, forward = false) => ({ x, y, forward });

/** Project encounter ownership without changing simulation. Axes are pilot-frame
 * permissions, not velocities. Readiness/sequence belong to caller preparation.
 * Missing/terminal states fail closed. Protected transit retains sight input;
 * only an authored combatReady route may grant live fire. Camera describes the
 * authored view, not whether a renderer blend has physically finished. */
export function skyFlightCapabilities(state, { ready = true, sequence = false } = {}) {
  const boss = state?.boss;
  const encounter = state?.leviathan ?? state?.cathedral;
  const ending = Boolean(
    boss?.defeated ||
    ['crash', 'complete', 'escaping', 'escaped', 'pilot-down', 'lost'].includes(encounter?.phase),
  );
  const active = state?.status === 'playing' && state.lives !== 0 && ready && !sequence && !ending;
  const bonus = SKY_STAGES[state?.sector]?.kind === 'bonus';
  const systems = encounter?.systems ?? boss?.systems;
  const route = state?.traversal;
  const transit = Boolean(
    route ||
    ['flyby', 'aim-approach'].includes(boss?.controlMode) ||
    systems?.protected ||
    systems?.controls === false,
  );
  const combatRoute = route?.combatReady === true;
  const attackRun =
    boss?.attackRun?.active === true && boss?.aimWindow === true && !boss?.aimPreparing;
  const preparing = Boolean(boss?.aimPreparing || boss?.controlMode === 'aim-approach');
  const manual = active && !transit && !attackRun && !preparing && systems?.controls !== false;
  const safe = (transit && !combatRoute) || preparing;
  const weapons = Boolean(
    active &&
    !bonus &&
    !state.cathedral?.motionHeld &&
    !safe &&
    (combatRoute || boss?.controlMode !== 'aim' || boss?.aimWindow !== false),
  );
  const offsets = manual && Boolean(encounter);
  const scripted = active && (transit || offsets || attackRun || preparing);
  const targets = [...(state?.enemies ?? []), ...(boss ? [boss] : [])];
  const targetVulnerable = targets.some(
    (target) =>
      target.hp > 0 &&
      !target.defeated &&
      target.collisionEnabled !== false &&
      target.vulnerable !== false &&
      target.kind !== 'pillar' &&
      (target.kind !== 'boss' || target.vulnerable === true),
  );
  const movementMode = !active
    ? 'inactive'
    : manual
      ? offsets
        ? 'offsets'
        : 'manual'
      : 'scripted';
  return {
    manual: axes(manual, manual, manual && state.freeFlight === true),
    scripted: axes(scripted && !manual, scripted && !manual, scripted),
    aim: active,
    weapons,
    targetVulnerable,
    protected: Boolean(active && transit && !combatRoute),
    movementMode,
    camera: route
      ? 'cinematic'
      : preparing
        ? 'acquiring'
        : boss?.controlMode === 'aim'
          ? 'pilot-eye'
          : 'chase',
    label: !active
      ? state?.status === 'paused'
        ? 'PAUSED'
        : ending
          ? 'ENDING'
          : 'PREPARING'
      : bonus
        ? 'SAFE PASSAGE'
        : !weapons
          ? scripted
            ? 'AUTO PILOT · WEAPONS SAFE'
            : 'WEAPONS SAFE'
          : offsets
            ? 'AUTO PILOT · STEER / DODGE'
            : scripted
              ? attackRun
                ? 'AUTO PILOT · WEAPONS READY'
                : 'AUTO PILOT'
              : '',
  };
}
